/**
 * Selector de periódicos de destino.
 *
 * Es la pieza donde se gana o se pierde el producto entero: si elegir quince
 * periódicos cuesta quince clics, no hemos quitado el trabajo repetitivo, lo
 * hemos movido. De ahí los grupos y el "todos".
 *
 * Y es donde se avisa **antes** de publicar de lo que va a fallar: un sitio sin
 * categoría asignada para la sección de la noticia, o uno que necesita
 * reconexión. El servidor lo rechazaría igual, pero enterarse al pulsar
 * "Publicar" es enterarse tarde.
 */

import type { Section, Site, SiteGroup } from '../lib/api.ts';

export interface DestinationPickerProps {
  sites: Site[];
  groups: SiteGroup[];
  selected: Set<string>;
  onChange: (next: Set<string>) => void;
  /** Sección elegida en la noticia, si hay alguna. */
  section: Section | null;
  /** Sitios que tienen mapeada esa sección. */
  mappedSiteIds: Set<string>;
}

/** Estados en los que publicar va a fallar seguro. */
const BLOCKED_STATUS: Record<string, string> = {
  needs_reauth: 'Necesita reconectarse',
  needs_plugin_update: 'Conector desactualizado',
  disabled: 'Deshabilitado',
};

export function DestinationPicker({
  sites,
  groups,
  selected,
  onChange,
  section,
  mappedSiteIds,
}: DestinationPickerProps) {
  const toggle = (siteId: string) => {
    const next = new Set(selected);

    if (next.has(siteId)) {
      next.delete(siteId);
    } else {
      next.add(siteId);
    }

    onChange(next);
  };

  /**
   * Un grupo se marca o se desmarca entero.
   *
   * Si ya están todos sus sitios, el clic los quita; si falta alguno, los añade.
   * Es lo que la gente espera de una casilla de grupo, y evita el estado
   * intermedio confuso de "pulsé el grupo y no pasó nada porque ya estaba".
   */
  const toggleGroup = (group: SiteGroup) => {
    const members = group.siteIds.filter((id) => sites.some((site) => site.id === id));
    const allSelected = members.length > 0 && members.every((id) => selected.has(id));
    const next = new Set(selected);

    for (const id of members) {
      if (allSelected) {
        next.delete(id);
      } else {
        next.add(id);
      }
    }

    onChange(next);
  };

  const selectable = sites.filter((site) => site.status !== 'disabled');
  const allSelected = selectable.length > 0 && selectable.every((site) => selected.has(site.id));

  // Solo cuentan los avisos de los sitios ELEGIDOS: enseñar que un periódico
  // que nadie seleccionó le falta el mapeo es ruido.
  const unmapped = section
    ? sites.filter((site) => selected.has(site.id) && !mappedSiteIds.has(site.id))
    : [];

  const blocked = sites.filter((site) => selected.has(site.id) && BLOCKED_STATUS[site.status]);

  return (
    <div>
      <div className="mb-3 flex flex-wrap items-center gap-2">
        <button
          type="button"
          onClick={() => onChange(allSelected ? new Set() : new Set(selectable.map((s) => s.id)))}
          className="rounded-full border border-[var(--color-line)] px-3 py-1 text-xs hover:bg-[var(--color-surface)]"
        >
          {allSelected ? 'Ninguno' : `Todos (${selectable.length})`}
        </button>

        {groups.map((group) => {
          const members = group.siteIds.filter((id) => sites.some((site) => site.id === id));
          const active = members.length > 0 && members.every((id) => selected.has(id));

          return (
            <button
              key={group.id}
              type="button"
              aria-pressed={active}
              onClick={() => toggleGroup(group)}
              className={`rounded-full border px-3 py-1 text-xs transition ${
                active
                  ? 'border-[var(--color-brand)] bg-[var(--color-brand)] text-white'
                  : 'border-[var(--color-line)] hover:bg-[var(--color-surface)]'
              }`}
            >
              {group.name} ({members.length})
            </button>
          );
        })}
      </div>

      <ul className="divide-y divide-[var(--color-line)] rounded-lg border border-[var(--color-line)]">
        {sites.map((site) => {
          const blockedReason = BLOCKED_STATUS[site.status];
          const missingMapping = Boolean(section) && !mappedSiteIds.has(site.id);
          const isSelected = selected.has(site.id);

          return (
            <li key={site.id} className="flex items-center gap-3 px-3 py-2.5">
              <input
                type="checkbox"
                id={`destino-${site.id}`}
                checked={isSelected}
                onChange={() => toggle(site.id)}
                // El `label` de al lado ya lo describe, pero su texto está en
                // dos `span` y el nombre accesible salía como "on" en el árbol.
                // Con quince casillas idénticas llamadas "on", esta pantalla es
                // inservible con lector de pantalla.
                aria-label={site.name}
                className="size-4 shrink-0"
              />

              <label htmlFor={`destino-${site.id}`} className="flex min-w-0 flex-1 flex-col">
                <span className="truncate text-sm font-medium">{site.name}</span>
                <span className="truncate text-xs text-[var(--color-muted)]">{site.baseUrl}</span>
              </label>

              <div className="flex shrink-0 items-center gap-1.5">
                {blockedReason ? (
                  <span className="rounded-full bg-red-50 px-2 py-0.5 text-xs text-[var(--color-danger)]">
                    {blockedReason}
                  </span>
                ) : null}

                {missingMapping && !blockedReason ? (
                  <span className="rounded-full bg-amber-50 px-2 py-0.5 text-xs text-amber-700">
                    Sin categoría asignada
                  </span>
                ) : null}

                {site.status === 'degraded' ? (
                  <span className="rounded-full bg-amber-50 px-2 py-0.5 text-xs text-amber-700">
                    Con fallos recientes
                  </span>
                ) : null}
              </div>
            </li>
          );
        })}
      </ul>

      {unmapped.length > 0 ? (
        <p role="alert" className="mt-3 rounded-lg bg-amber-50 px-3 py-2 text-sm text-amber-800">
          {unmapped.length === 1
            ? `${unmapped[0]?.name} no tiene una categoría asignada para «${section?.name}».`
            : `${unmapped.length} sitios no tienen categoría asignada para «${section?.name}».`}{' '}
          Asígnala en Sitios → Categorías o quítalos de la selección; si no, la noticia no saldrá en ellos.
        </p>
      ) : null}

      {blocked.length > 0 ? (
        <p role="alert" className="mt-3 rounded-lg bg-red-50 px-3 py-2 text-sm text-[var(--color-danger)]">
          {blocked.map((site) => site.name).join(', ')}{' '}
          {blocked.length === 1 ? 'no puede recibir' : 'no pueden recibir'} publicaciones ahora mismo.
        </p>
      ) : null}
    </div>
  );
}
